import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Loader2, Plus, X } from "lucide-react"
import { useDeckCreation } from "@/hooks/useDeckCreation"
import { getTokenBalance, listThemes } from "@/services/api"
import { Card, CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { UserTier } from "@/types/rateLimit"
import { DECK_LIMITS } from "@/types/deckLimits"
import { Theme } from "@/types/themes"
import { DeckMetadata } from "@/types/decks"

export function DeckBuilder() {
  const navigate = useNavigate()
  const { loading, createNewDeck } = useDeckCreation()
  const [themes, setThemes] = useState<Theme[]>([])
  const [userTier, setUserTier] = useState<UserTier>(UserTier.FREE)
  const [newTag, setNewTag] = useState("")
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    themeId: "",
    universeId: 0,
    is_public: true,
    is_collaborative: false,
    metadata: {
      categories: [],
      tags: [],
      customFields: {}
    } as DeckMetadata
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tierResponse, themesResponse] = await Promise.all([
          getTokenBalance(),
          listThemes()
        ])
        setUserTier(tierResponse.data.data.tier as UserTier)
        setThemes(themesResponse.data.data)
      } catch (error) {
        console.error('Failed to fetch data:', error)
      }
    }

    fetchData()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.themeId) {
      return
    }

    if (!formData.is_public && !DECK_LIMITS[userTier].canCreatePrivateDeck) {
      return
    }

    if (formData.is_collaborative && !DECK_LIMITS[userTier].canCreateCollaborativeDeck) {
      return
    }

    await createNewDeck(formData)
  }

  const addTag = () => {
    if (newTag && !formData.metadata.tags?.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          tags: [...(prev.metadata.tags || []), newTag]
        }
      }))
      setNewTag("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        tags: prev.metadata.tags?.filter((tag: string) => tag !== tagToRemove) || []
      }
    }))
  }

  const selectedTheme = themes.find(t => t.id === formData.themeId)
  const limits = DECK_LIMITS[userTier]

  return (
    <div className="p-4 md:p-8 md:ml-[256px]">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/decks')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Create New Deck</h1>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Deck Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="theme">Theme</Label>
              <Select
                value={formData.themeId}
                onValueChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  themeId: value 
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a theme" />
                </SelectTrigger>
                <SelectContent>
                  {themes.map((theme) => (
                    <SelectItem key={theme.id} value={theme.id}>
                      <div className="flex flex-col">
                        <span>{theme.title}</span>
                        {theme.card_types && (
                          <span className="text-xs text-muted-foreground">
                            {JSON.parse(theme.card_types).map((t: any) => t.name).join(', ')}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="public">Private Deck</Label>
                  <p className="text-sm text-muted-foreground">
                    Make this deck only visible to you
                  </p>
                </div>
                <Switch
                  id="public"
                  checked={!formData.is_public}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    is_public: !checked 
                  }))}
                />
              </div>

              {!formData.is_public && !limits.canCreatePrivateDeck && (
                <Alert>
                  <AlertDescription>
                    Upgrade your account to create private decks
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="collaborative">Collaborative Deck</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow other users to add cards
                  </p>
                </div>
                <Switch
                  id="collaborative"
                  checked={formData.is_collaborative}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    is_collaborative: checked 
                  }))}
                />
              </div>

              {formData.is_collaborative && !limits.canCreateCollaborativeDeck && (
                <Alert>
                  <AlertDescription>
                    Upgrade your account to create collaborative decks
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.metadata.tags?.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addTag()
                      }
                    }}
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {selectedTheme?.card_types && (
                <div className="space-y-2">
                  <Label>Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {JSON.parse(selectedTheme.card_types).map((type: any) => (
                      <Badge
                        key={type.name}
                        variant={formData.metadata.categories?.includes(type.name) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            metadata: {
                              ...prev.metadata,
                              categories: prev.metadata.categories?.includes(type.name)
                                ? prev.metadata.categories.filter((t: string) => t !== type.name)
                                : [...(prev.metadata.categories || []), type.name]
                            }
                          }))
                        }}
                      >
                        {type.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Deck'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
} 